import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahbb3joh653ivfvnqa0";

type Status = "idle" | "loading" | "success" | "error";

const types = ["线索投稿", "纠错反馈", "合作洽谈", "订阅与内容咨询"];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [count, setCount] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("company_alt") ?? "").trim();
    payload.delete("company_alt");

    if (honeypot) {
      setStatus("success");
      setMessage("已收到，我们会在三个工作日内通过你留下的邮箱回复。");
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = String(
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || ""
      );
      const failed =
        !response.ok || String(parsed?.code || "") !== "OK" || /spam/i.test(serverMsg);

      if (failed) {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请检查内容后重试。");
        return;
      }

      setStatus("success");
      setMessage("已收到，我们会在三个工作日内通过你留下的邮箱回复。");
      form.reset();
      setCount(0);
    } catch {
      setStatus("error");
      setMessage("网络异常，提交未成功，请稍后再试或直接发邮件给我们。");
    }
  };

  return (
    <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-900 mb-2">
            称呼 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            required
            autoComplete="name"
            placeholder="怎么称呼你"
            className="w-full px-4 py-2.5 text-sm rounded-control border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-900 mb-2">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="用于接收回复"
            className="w-full px-4 py-2.5 text-sm rounded-control border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-type" className="block text-sm font-medium text-foreground-900 mb-2">
          事项类型
        </label>
        <select
          id="contact-type"
          name="type"
          defaultValue={types[0]}
          className="w-full px-4 py-2.5 text-sm rounded-control border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
        >
          {types.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-900">
            详细说明 <span className="text-primary-500">*</span>
          </label>
          <span className={`text-xs ${count > 500 ? "text-primary-600" : "text-foreground-400"}`}>
            {count} / 500
          </span>
        </div>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={6}
          onChange={(e) => setCount(e.target.value.length)}
          placeholder="请描述线索或问题，尽量附上可核验的材料、时间与出处，便于我们判断。"
          className="w-full px-4 py-3 text-sm rounded-control border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 resize-y"
        ></textarea>
      </div>

      <input
        type="text"
        name="company_alt"
        className="form-honeypot-redirect"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "loading"}
          className="px-6 py-3 rounded-control bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60"
        >
          {status === "loading" ? "提交中…" : "提交"}
        </button>
        <p className="text-xs text-foreground-500 leading-relaxed">
          提交即表示你确认所提供的信息不涉及个人隐私与未成年人相关内容。
        </p>
      </div>

      {message && (
        <div
          role="status"
          className={`flex items-start gap-2 rounded-control border p-4 text-sm leading-relaxed ${
            status === "error"
              ? "border-primary-200 bg-primary-50 text-primary-700"
              : "border-secondary-200 bg-secondary-50 text-secondary-800"
          }`}
        >
          <i className={status === "error" ? "ri-error-warning-line mt-0.5" : "ri-checkbox-circle-line mt-0.5"}></i>
          <span>{message}</span>
        </div>
      )}
    </form>
  );
}